import { Controller } from "@hotwired/stimulus"

let chartLibrary

function loadChart() {
  if (window.Chart) return Promise.resolve(window.Chart)
  if (!chartLibrary) {
    chartLibrary = new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = "https://cdn.jsdelivr.net/npm/chart.js"
      script.onload = () => resolve(window.Chart)
      script.onerror = () => {
        script.remove()
        chartLibrary = null
        reject(new Error("Unable to load Chart.js"))
      }
      document.head.appendChild(script)
    })
  }
  return chartLibrary
}

export default class extends Controller {
  static values = { ratings: Array, average: Number }

  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return
    const connection = this.connection = {}
    loadChart().then(Chart => {
      if (this.connection === connection) this.render(Chart)
    }).catch(error => { if (this.connection === connection) console.warn(error) })
  }

  disconnect() {
    this.teardown()
  }

  teardown() {
    this.connection = null
    this.chart?.destroy()
    this.chart = null
  }

  render(Chart) {
    const data = this.ratingsValue
    const average = this.averageValue
    // Determine color scheme based on average rating
    // 3-4: Green, 1-2: Orange, 0: Red
    let colors;
    if (average >= 2.5) {
        colors = {
            lightest: 'rgba(209, 250, 229, 0.9)', // Very pale mint
            medium: 'rgba(167, 243, 208, 0.9)',   // Light seafoam
            darkest: 'rgba(110, 231, 183, 0.9)'   // Darker teal/sage
        };
    } else if (average >= 1) {
        colors = {
            lightest: 'rgba(254, 243, 199, 0.9)', // amber-100
            medium: 'rgba(253, 230, 138, 0.9)',   // amber-200
            darkest: 'rgba(252, 211, 77, 0.9)'    // amber-300
        };
    } else {
        colors = {
            lightest: 'rgba(254, 226, 226, 0.9)', // red-100
            medium: 'rgba(254, 202, 202, 0.9)',   // red-200
            darkest: 'rgba(252, 165, 165, 0.9)'   // red-300
        };
    }

    // Extract data: [label_obj, skin_rating, min_concern, max_concern]
    // Label object has: {day: "Tue", date: "28", current: false}
    const labelObjects = data.map(d => d[0]);
    const labels = labelObjects.map(l => [l.day, l.date]); // Two-line labels
    const skinRatings = data.map(d => d[1]);
    const minConcerns = data.map(d => d[2]);
    const maxConcerns = data.map(d => d[3]);

    // Calculate intermediate bands that follow the curve
    const band1 = data.map(d => {
        const min = d[2];
        const max = d[3];
        const range = max - min;
        return min + (range * 0.33);
    });

    const band2 = data.map(d => {
        const min = d[2];
        const max = d[3];
        const range = max - min;
        return min + (range * 0.66);
    });

    const chartConfig = {
        type: 'line',
        data: {
            labels: labels,
            datasets: [
                // Bottom baseline (min concerns) - invisible
                {
                    data: minConcerns,
                    borderColor: 'transparent',
                    borderWidth: 0,
                    pointRadius: 0,
                    tension: 0.4,
                    fill: false,
                    order: 5
                },
                // First band (lightest) - fills from min to 33%
                {
                    data: band1,
                    borderColor: 'transparent',
                    borderWidth: 0,
                    pointRadius: 0,
                    tension: 0.4,
                    backgroundColor: colors.lightest,
                    fill: '-1',
                    order: 4
                },
                // Second band (medium) - fills from 33% to 66%
                {
                    data: band2,
                    borderColor: 'transparent',
                    borderWidth: 0,
                    pointRadius: 0,
                    tension: 0.4,
                    backgroundColor: colors.medium,
                    fill: '-1',
                    order: 3
                },
                // Third band (darkest) - fills from 66% to max
                {
                    data: maxConcerns,
                    borderColor: 'transparent',
                    borderWidth: 0,
                    pointRadius: 0,
                    tension: 0.4,
                    backgroundColor: colors.darkest,
                    fill: '-1',
                    order: 2
                },
                // Main skin rating line - rendered ON TOP
                {
                    data: skinRatings,
                    borderColor: 'rgb(17, 24, 39)', // Near black
                    borderWidth: 2.5,
                    tension: 0,
                    spanGaps: true, // Connect across null values (missing dates)
                    pointBackgroundColor: 'rgb(17, 24, 39)', // Near black for all dots
                    pointRadius: function (context) {
                        // Don't show point if value is null
                        return context.parsed.y === null ? 0 : 6;
                    },
                    pointBorderWidth: 0,
                    fill: false,
                    order: 1 // Lowest order = drawn on top
                }
            ]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false, // Disable animation
            events: [], // Disable all mouse/touch events
            layout: {
                padding: {
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0
                }
            },
            interaction: {
                mode: 'none'
            },
            scales: {
                x: {
                    offset: false, // Don't add extra space before first and after last point
                    grid: {
                        display: false,
                        drawBorder: false
                    },
                    ticks: {
                        font: function (context) {
                            const index = context.index;
                            const isCurrent = labelObjects[index]?.current;
                            return {
                                size: 11,
                                weight: isCurrent ? 700 : 400 // 700 = bold, 400 = normal
                            };
                        },
                        color: function (context) {
                            const index = context.index;
                            const isCurrent = labelObjects[index]?.current;
                            return isCurrent ? 'rgb(28, 25, 23)' : 'rgb(120, 113, 108)'; // warm-gray-900 : warm-gray-500
                        }
                    }
                },
                y: {
                    min: -1,
                    max: 5,
                    display: false,
                    grid: {
                        drawBorder: false
                    }
                }
            },
            plugins: {
                legend: {
                    display: false
                },
                tooltip: {
                    enabled: false
                },
                filler: {
                    propagate: false
                }
            }
        }
    };

    this.chart = new Chart(this.element, chartConfig)
  }
}
